import React from "react";
import { render, screen } from "@testing-library/react";
import "@testing-library/jest-dom";
import { diffLines } from "diff";

import VersionDiffView, {
  DIFF_TOO_LARGE_MESSAGE,
  DIFF_UNAVAILABLE_MESSAGE,
  MAX_DIFF_INPUT_CHARACTERS,
  MAX_RENDERED_DIFF_ROWS,
} from "./VersionDiffView";

jest.mock("diff", () => {
  const actual = jest.requireActual<typeof import("diff")>("diff");
  return { ...actual, diffLines: jest.fn(actual.diffLines) };
});

const mockDiffLines = jest.mocked(diffLines);

const mockDownloadDocument = jest.fn();
const mockGetDocumentDiffText = jest.fn();

jest.mock("../services/api", () => ({
  useDownloadDocument: () => mockDownloadDocument,
  useGetDocumentDiffText: () => mockGetDocumentDiffText,
}));

const renderDiff = (
  overrides: Partial<React.ComponentProps<typeof VersionDiffView>> = {},
) =>
  render(
    <VersionDiffView
      workspaceId="ws-1"
      currentVersionId="ws-1/v2"
      selectedVersionId="ws-1/v1"
      currentFilename="Policy.docx"
      selectedFilename="Policy.docx"
      currentLabel="Version 2 (Current)"
      selectedLabel="Version 1"
      {...overrides}
    />,
  );

const wordText = (docId: string, text: string) => ({
  doc_id: docId,
  filename: "Policy.docx",
  text,
});

describe("VersionDiffView", () => {
  beforeEach(() => {
    mockDownloadDocument.mockReset();
    mockGetDocumentDiffText.mockReset();
    mockDiffLines.mockClear();
    mockDiffLines.mockImplementation(
      jest.requireActual<typeof import("diff")>("diff").diffLines,
    );
  });

  it("diffs the text of two Word versions", async () => {
    mockGetDocumentDiffText.mockImplementation(
      async (_workspaceId: string, docId: string) =>
        docId === "ws-1/v2"
          ? wordText(
              docId,
              "# Policy\nRemote work is allowed.\n| Owner | Alex |",
            )
          : wordText(docId, "# Policy\nRemote work is not allowed."),
    );

    renderDiff();

    expect(await screen.findByText("Changes found")).toBeInTheDocument();
    expect(screen.getByText("Remote work is not allowed.")).toBeInTheDocument();
    expect(screen.getByText("Remote work is allowed.")).toBeInTheDocument();
    expect(screen.getByText("| Owner | Alex |")).toBeInTheDocument();
    expect(
      screen.getByText(/Comparing the text of the Word document/),
    ).toBeInTheDocument();
    expect(mockGetDocumentDiffText).toHaveBeenCalledWith("ws-1", "ws-1/v2");
    expect(mockGetDocumentDiffText).toHaveBeenCalledWith("ws-1", "ws-1/v1");
    expect(mockDownloadDocument).not.toHaveBeenCalled();
  });

  it("reads SharePoint Word versions through the document and its version ids", async () => {
    mockGetDocumentDiffText.mockResolvedValue(
      wordText("ws-1/sp", "Same text."),
    );

    renderDiff({
      currentVersionId: "2.0",
      selectedVersionId: "1.0",
      sharePointDocId: "ws-1/sp",
    });

    expect(await screen.findByText("No text changes")).toBeInTheDocument();
    expect(mockGetDocumentDiffText).toHaveBeenCalledWith("ws-1", "ws-1/sp", {
      spVersionId: "2.0",
    });
    expect(mockGetDocumentDiffText).toHaveBeenCalledWith("ws-1", "ws-1/sp", {
      spVersionId: "1.0",
    });
  });

  it("shows the error when a Word version cannot be read", async () => {
    mockGetDocumentDiffText.mockRejectedValue(
      new Error("This Word document could not be read for comparison."),
    );

    renderDiff();

    expect(
      await screen.findByText(
        "This Word document could not be read for comparison.",
      ),
    ).toBeInTheDocument();
  });

  it("skips diffing when the combined input exceeds the line budget", async () => {
    mockGetDocumentDiffText.mockImplementation(
      async (_workspaceId: string, docId: string) =>
        wordText(
          docId,
          Array.from(
            { length: docId === "ws-1/v2" ? 2_500 : 2_501 },
            (_, i) => `line ${i}`,
          ).join("\n"),
        ),
    );

    renderDiff();

    expect(await screen.findByText(DIFF_TOO_LARGE_MESSAGE)).toBeInTheDocument();
    expect(mockDiffLines).not.toHaveBeenCalled();
    expect(screen.queryByTestId("version-diff-row")).not.toBeInTheDocument();
  });

  it("skips diffing when a long line exceeds the combined character budget", async () => {
    mockGetDocumentDiffText.mockImplementation(
      async (_workspaceId: string, docId: string) =>
        wordText(
          docId,
          "x".repeat(
            docId === "ws-1/v2"
              ? MAX_DIFF_INPUT_CHARACTERS / 2
              : MAX_DIFF_INPUT_CHARACTERS / 2 + 1,
          ),
        ),
    );

    renderDiff();

    expect(await screen.findByText(DIFF_TOO_LARGE_MESSAGE)).toBeInTheDocument();
    expect(mockDiffLines).not.toHaveBeenCalled();
  });

  it("caps rendered diff rows and explains that the output is truncated", async () => {
    mockGetDocumentDiffText.mockImplementation(
      async (_workspaceId: string, docId: string) =>
        wordText(
          docId,
          Array.from(
            { length: 600 },
            (_, i) =>
              `${docId === "ws-1/v2" ? "current" : "previous"} line ${i}`,
          ).join("\n"),
        ),
    );

    renderDiff();

    expect(await screen.findByText("Changes found")).toBeInTheDocument();
    expect(screen.getAllByTestId("version-diff-row")).toHaveLength(
      MAX_RENDERED_DIFF_ROWS,
    );
    expect(
      screen.getByText(
        `Showing first ${MAX_RENDERED_DIFF_ROWS.toLocaleString()} changes.`,
      ),
    ).toBeInTheDocument();
  });

  it("still diffs text files from their downloaded content", async () => {
    // jsdom's Blob has no text(); browsers' does.
    const textBlob = (content: string) =>
      Object.assign(new Blob([content]), { text: async () => content });
    mockDownloadDocument.mockImplementation(
      async (_workspaceId: string, docId: string) =>
        textBlob(docId === "ws-1/v2" ? "line two\n" : "line one\n"),
    );

    renderDiff({ currentFilename: "notes.txt", selectedFilename: "notes.txt" });

    expect(await screen.findByText("Changes found")).toBeInTheDocument();
    expect(mockGetDocumentDiffText).not.toHaveBeenCalled();
    expect(
      screen.queryByText(/Comparing the text of the Word document/),
    ).not.toBeInTheDocument();
  });

  it.each([
    ["Report.pdf", "Report.pdf"],
    ["Legacy.doc", "Legacy.doc"],
    ["Policy.docx", "notes.txt"],
  ])(
    "explains that %s vs %s cannot be diffed",
    async (currentFilename, selectedFilename) => {
      renderDiff({ currentFilename, selectedFilename });

      expect(
        await screen.findByText(DIFF_UNAVAILABLE_MESSAGE),
      ).toBeInTheDocument();
      expect(mockGetDocumentDiffText).not.toHaveBeenCalled();
      expect(mockDownloadDocument).not.toHaveBeenCalled();
    },
  );
});
